import { useQuery } from '@tanstack/react-query'

import { queryKeys } from '@sportninja/common/config/queryKeys'
import type { GameLike } from '@sportninja/common/features/game/gameListView/types'
import {
  officialDisplayName,
  searchAdminOfficials,
} from '@sportninja/common/services/official/searchAdminOfficials'
import type { AdminOfficialSearchResult } from '@sportninja/common/services/official/searchAdminOfficials'

/** One person who can be assigned to a game. */
export interface OfficialOption {
  /** Official uid. */
  id: string
  name: string
  /** The role stored on the PERSON — seeds a new assignment, nothing more. */
  defaultTypeId: number | null
  /** Uid of the linked app account, when the invite was accepted. */
  userId: string | null
  /** Opaque key for the same person across records that share no account. */
  identityHash: string | null
}

export interface UseOfficialOptionsResult {
  /** What the picker offers: one entry per person, duplicates collapsed. */
  officials: OfficialOption[]
  /** EVERY known record, uncollapsed — labels must resolve for all of them. */
  officialsById: Map<string, OfficialOption>
  /** uid → display name. Flat and row-independent — see below. */
  officialNamesById: Map<string, string>
  isLoading: boolean
  /** False when the pool cannot be fetched, which makes the columns read-only. */
  canAssign: boolean
}

const addOfficial = (
  map: Map<string, OfficialOption>,
  option: OfficialOption
) => {
  if (option.id === '') {
    return
  }
  const previous = map.get(option.id)
  map.set(option.id, {
    id: option.id,
    //  Prefer whichever source actually has a name: a game row carries one, the
    //  search pool carries one, but either can arrive bare.
    name: option.name !== '' ? option.name : previous?.name ?? '',
    defaultTypeId: option.defaultTypeId ?? previous?.defaultTypeId ?? null,
    userId: option.userId ?? previous?.userId ?? null,
    identityHash: option.identityHash ?? previous?.identityHash ?? null,
  })
}

/**
 * One option per PERSON, not per record.
 *
 * Officials duplicate: an import or a re-invite leaves one record linked to the
 * organization and another to the competition, both pointing at the same app
 * account. Offering both gives the admin two identical-looking choices, neither
 * of which is wrong.
 *
 * Collapsed on the linked ACCOUNT, then on the server's opaque identity key (a
 * hash of the email) — never on the name. Both are needed: the common duplicate
 * is one record linked to the organization and one to the competition sharing an
 * account, but plenty of pairs have an account on only one side (Darcy Fiedler:
 * 96 games on the unlinked record, 37 on the linked one, one email between them)
 * and only the identity key can join those. The name alone would be wrong — two
 * genuinely distinct accounts share the name "Rob Larsen", with different
 * emails, and merging them would assign games to the wrong person.
 *
 * A record with neither an account nor an identity key is never merged, because
 * nothing proves it is the same human.
 *
 * The survivor is whichever record this competition's games ALREADY use, so an
 * existing assignment keeps its own record rather than being quietly rewritten
 * to the other one on the next edit.
 */
const collapseByAccount = (
  officials: OfficialOption[],
  inUse: Set<string>
): OfficialOption[] => {
  const chosen = new Map<string, OfficialOption>()
  const kept: OfficialOption[] = []

  //  Account first, email second. An identity that resolves to neither cannot
  //  be merged with anything.
  const identitiesOf = (official: OfficialOption): string[] =>
    [
      official.userId === null ? null : `user:${official.userId}`,
      official.identityHash === null ? null : `person:${official.identityHash}`,
    ].filter((identity): identity is string => identity !== null)

  for (const official of officials) {
    const identities = identitiesOf(official)
    if (identities.length === 0) {
      kept.push(official)
      continue
    }

    const previous = identities
      .map((identity) => chosen.get(identity))
      .find((candidate) => candidate !== undefined)

    if (previous === undefined) {
      identities.forEach((identity) => chosen.set(identity, official))
      kept.push(official)
      continue
    }

    //  Already have one for this person. Swap only when THIS record is the one
    //  the games use and the incumbent is not — and re-point every identity at
    //  the survivor, so a third record matching on either key finds it.
    if (inUse.has(official.id) && !inUse.has(previous.id)) {
      const index = kept.indexOf(previous)
      if (index !== -1) {
        kept[index] = official
      }
      identitiesOf(previous).forEach((identity) =>
        chosen.set(identity, official)
      )
      identities.forEach((identity) => chosen.set(identity, official))
    } else {
      identities.forEach((identity) => {
        if (!chosen.has(identity)) {
          chosen.set(identity, previous)
        }
      })
    }
  }

  return kept
}

const fromSearchResult = (
  official: AdminOfficialSearchResult
): OfficialOption => ({
  id: official.id,
  name: officialDisplayName(official),
  defaultTypeId:
    official.official_type == null ? null : Number(official.official_type),
  userId: official.user_id ?? null,
  identityHash: official.identity_hash ?? null,
})

/**
 * The officials an admin can assign, for the Details grid's three columns.
 *
 * Unions TWO sources, and the second is not optional:
 *
 * 1. the organization + competition pool from `/search/admin/officials`,
 * 2. every official already assigned on a loaded game.
 *
 * (2) matters because the pool and reality disagree. On one real competition 51
 * distinct officials appear in games while only 29 are linked to the schedule
 * and 28 to the organization — creating an official on a game writes no
 * organization or schedule link, so they are invisible to the search endpoint
 * forever after. Without the union those people render as a bare uid in a cell
 * that plainly has a name in the database.
 *
 * Both scope params are required before firing: unscoped, that endpoint returns
 * every official on the platform.
 */
export const useOfficialOptions = (
  organizationId: string | null | undefined,
  scheduleId: string | null | undefined,
  loadedGames: GameLike[] = []
): UseOfficialOptionsResult => {
  'use memo'

  const canAssign = Boolean(organizationId) && Boolean(scheduleId)

  const query = useQuery({
    queryKey: queryKeys.adminOfficialSearch.byScope(organizationId, scheduleId),
    queryFn: ({ signal }) =>
      searchAdminOfficials({
        organizationId: organizationId as string,
        scheduleId: scheduleId as string,
        signal,
      }),
    enabled: canAssign,
    //  A directory of people, not game state: it barely moves within a session.
    staleTime: 5 * 60_000,
  })

  const officialsById = new Map<string, OfficialOption>()

  for (const official of query.data ?? []) {
    addOfficial(officialsById, fromSearchResult(official))
  }
  //  Records this competition's loaded games actually reference. Decides which
  //  of a duplicated pair survives the collapse below.
  const inUse = new Set<string>()
  for (const game of loadedGames) {
    for (const official of game.officials ?? []) {
      inUse.add(official.id)
      addOfficial(officialsById, {
        id: official.id,
        name: officialDisplayName(official),
        //  A game row reports the role for THAT game, which is not the person's
        //  default — so it must never be read as one.
        defaultTypeId: null,
        userId: null,
        //  The games list carries no identity key; the pool entry supplies it.
        identityHash: null,
      })
    }
  }

  const officials = collapseByAccount(
    Array.from(officialsById.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    ),
    inUse
  )

  /*
   * Flat and row-independent on purpose. This labels the cell AND the column's
   * filter and tooltip, and AG Grid hands a column formatter no row when it is
   * formatting anything other than a cell — a lookup that reached for the row
   * would render the same label everywhere it was used.
   */
  //  Built from EVERY record, not the collapsed list: a game may reference the
  //  record that lost the collapse, and its cell still has to render a name.
  const officialNamesById = new Map<string, string>()
  for (const official of officialsById.values()) {
    if (official.name !== '') {
      officialNamesById.set(official.id, official.name)
    }
  }

  return {
    officials,
    officialsById,
    officialNamesById,
    isLoading: query.isLoading,
    canAssign,
  }
}
